import React, {useState} from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getGroupedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    GroupingState,
    PaginationState, Row,
    SortingState,
    TableOptions,
    useReactTable,
} from '@tanstack/react-table';
import {Table, TableBody, TableCell, TableHeader, TableRow,} from "../../cnComponents/cnTable";
import {SearchField} from "../search/searchField";
import {EmblaIcons} from "../../emblaIcons";
import './ReactTableStyles.scss'
import classNames from "classnames";
import {Loading} from "../loading/loading";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {Pagination} from "../pagination/pagination";
import {PaginationModel} from "../../sharedmodels/filter/PaginationModel";
import Container from "../container/container";
import {Dropdown, DropdownOption} from "../dropdown/dropdown";
import DynamicButton from "../button/dynamicButton";
import {createPortal} from "react-dom";
import {SlideIn} from "../slideIn/slideIn";
import TableColumnSettings from "./TableColumnSettings";
import {arrayMove, horizontalListSortingStrategy, SortableContext} from "@dnd-kit/sortable";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers'
import {ReactSyncTablePaginationCount} from "./ReactSyncTablePaginationCount";
import {DragAlongCell} from "./DragAlongCell";
import ReactTableHeader from "./ReactTableHeader";
import FilterNotation from "../filter/components/filterNotation";
import {EmblaIcon} from "../emblaIcon/emblaIcon";

export type NoResultsOptions = {
    noResultsMessage: string;
    noResultsIllustrationName?: string;
};

export type InitialFilterModel = {
    initialFilterId: string;
    initialFilterValue: string | boolean | [number, number] | string[];
}

export type FilterOptions = {
    hidePageSizeFilter?: boolean;
    pageSizeOptions: number[];
    initialSortColumn?: string;
    initialFilter?: InitialFilterModel[];
    onPageSizeChangedCallback?: (pageSize: number) => void;
    hideSearchLabel?: boolean;
    onSearchChangeCallback?: (searchQuery: string) => void;
    hideColumnFilter?: boolean;
};

export type ReactTablePaginationProps = {
    pageIndex: number;
    pageLength: number;
    totalRecords: number;
    onPageChangeCallback: (page: number) => void;
}

export type DefaultTableProps<TColumn extends object> = {
    title?: string;
    rowOnClick?: (rowData: TColumn) => void;
    noResultsOptions?: NoResultsOptions;
} & Pick<TableOptions<TColumn>, "columns" | "data">;

export type ReactTableProps<T> = {
    data: T[];
    columns: ColumnDef<T>[];
    isLoading: boolean;
    title?: string;
    isSortable?: boolean;
    enableColumnGrouping?: boolean;
    enableColumnResizing?: boolean;
    isAsync?: boolean;
    onSortCallback?: (sortInfos: SortingState) => void;
    onGroupingCallback?: (grouping: GroupingState) => void;
    paginationOverride?: ReactTablePaginationProps;
    filterOptions: FilterOptions;
    defaultExpandedGroups: boolean;
    rowColorCallback?: (row: Row<T>) => string
}

export const multiSelectFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    return filterValue.length === 0 || filterValue.includes(value);
};

export const getCellTooltipId = (rowIndex: number, cellId: string): string => {
    return `tooltip-${rowIndex}-${cellId}`;
};

const columnSettingsModalTargetId = "column-settings-modal";

function getFilterPil(filterValue: string | [number | undefined, number | undefined] | boolean) {
    return <>
        {Array.isArray(filterValue)
            ? filterValue.join(', ')
            : filterValue === true
                ? Localizer.global_ja()
                : filterValue === false
                    ? Localizer.global_nej()
                    : filterValue?.toString()}
    </>;
}

const ReactBaseTable = <T, >({
                                 data,
                                 columns,
                                 isLoading,
                                 paginationOverride,
                                 title,
                                 onSortCallback,
                                 onGroupingCallback,
                                 filterOptions,
                                 enableColumnGrouping,
                                 enableColumnResizing,
                                 isAsync = false,
                                 defaultExpandedGroups,
                                 rowColorCallback
                             }: ReactTableProps<T>) => {

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(filterOptions.initialFilter
        ?
        filterOptions.initialFilter.map((filterOptions: InitialFilterModel) => ({
            id: filterOptions.initialFilterId,
            value: filterOptions.initialFilterValue,
        }))
        :
        []);
    const [columnVisibility, setColumnVisibility] = useState({})
    const [sorting, setSorting] = useState<SortingState>(filterOptions.initialSortColumn ? [{
        id: filterOptions.initialSortColumn,
        desc: false
    }] : []);
    const [columnSlideInIsOpen, setColumnSlideInIsOpen] = useState<boolean>(false);
    const [columnOrder, setColumnOrder] = React.useState<string[]>(() => columns.map(c => c.id!))
    const [grouping, setGrouping] = React.useState<GroupingState>([])
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: filterOptions.pageSizeOptions[0],
    })

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            columnOrder,
            grouping,
            pagination,
        },
        onSortingChange: !isAsync
            ? setSorting
            : (newSorting) => {
                const updatedSorting = typeof newSorting === 'function' ? newSorting(sorting) : newSorting;
                setSorting(updatedSorting);
                if (onSortCallback) {
                    onSortCallback(updatedSorting);
                }
            },
        onGroupingChange: !isAsync
            ? setGrouping
            : (newGrouping) => {
                const updatedGrouping = typeof newGrouping === 'function' ? newGrouping(grouping) : newGrouping;
                setGrouping(updatedGrouping);
                if (onGroupingCallback) {
                    onGroupingCallback(updatedGrouping);
                }
            },
        onColumnOrderChange: setColumnOrder,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getExpandedRowModel: getExpandedRowModel(),
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getGroupedRowModel: getGroupedRowModel(),

        getFacetedRowModel: getFacetedRowModel(), // client-side faceting
        getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
        getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
        manualPagination: isAsync,
        manualFiltering: isAsync,
        manualSorting: isAsync,
        columnResizeMode: 'onChange',

        //Snak med kunden - skal vi enableSortingRemoval, som betyder: Man kan kun sortere asc & desc
        enableSortingRemoval: false,
        //Hvis vi gør det, skal vi ikke have mulighed for multisorting (sortering på flere kolonner) - da det i så fald ikke giver mening - demonstrer
        // enableMultiSort: true,
        // isMultiSortEvent: (e) => true,
        initialState: {
            expanded: defaultExpandedGroups ? true : {},
        }
    });

    const pageSizeOptions = filterOptions.pageSizeOptions.map(x => ({
        label: `${x}`,
        value: x
    } as DropdownOption<number>));

    const handlePageSizeChanged = (pageLength?: number) => {
        if (filterOptions.onPageSizeChangedCallback && pageLength)
            filterOptions.onPageSizeChangedCallback(pageLength);
    }

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event
        const overColumn = table.getColumn(over?.id as string);
        if (active && over && active.id !== over.id && !overColumn?.getIsPinned()) {
            setColumnOrder(columnOrder => {
                const oldIndex = columnOrder.indexOf(active.id as string)
                const newIndex = columnOrder.indexOf(over.id as string)
                return arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
            })
        }
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    )

    return (
        <div className={"card"}>
            {title &&
                <Container.Header>
                    <h2 className="">{title}</h2>
                </Container.Header>
            }
            <Container.Body>
                <div className={classNames("flex-container-row wrap padding-bottom-m align-items-center")}>
                    {!filterOptions.hidePageSizeFilter &&
                        <Dropdown
                            minWidth={50}
                            options={pageSizeOptions}
                            onChange={(selectedOption) => isAsync ? handlePageSizeChanged(selectedOption?.value) : table.setPageSize(Number(selectedOption?.value))}
                            value={isAsync ? pageSizeOptions.find(x => x.value === paginationOverride?.pageLength) : pageSizeOptions.find(x => x.value === table.getState().pagination.pageSize)}
                            isClearable={false}
                            isSearchable={false}
                        />
                    }
                    <FilterNotation
                        paginationModel={new PaginationModel(table.getState().pagination.pageIndex + 1, table.getState().pagination.pageSize, table.getRowCount())}
                        filteredRecords={table.getPreGroupedRowModel().rows.length}
                        customTotalRecords={data.length}
                        xOfYisLoading={isLoading}
                        hidePaginatedCount={false}
                        additionalContainerClasses="d-flex flex-column x-of-y-font-small margin-left-s"
                    />

                    <div className={"flex-container-row flex-align-r align-items-center wrap"}>
                        {table.getAllColumns().map(column => {
                            const filterValue = column.getFilterValue() as string | boolean | [number | undefined, number | undefined];
                            if ((filterValue !== undefined && !Array.isArray(filterValue)) || (Array.isArray(filterValue) && filterValue?.some(x => x))) {
                                return (
                                    <span key={column.id}
                                          className="margin-right-s badge badge-pill badge-info-light padding-s d-flex margin-top-xs">
                                        <b>{column.columnDef.header}: &nbsp;</b> {getFilterPil(filterValue)}
                                        <div style={{cursor: 'pointer'}} onClick={() => {
                                            setColumnFilters((prevFilters) => prevFilters.filter(filter => filter.id !== column.id));
                                        }}>
                                            <EmblaIcon additionalClasses={"margin-left-s align-items-center"}
                                                       iconName={EmblaIcons.Close}/>
                                        </div>
                                    </span>
                                );
                            }
                            return null;
                        })}
                        {table.getState().columnFilters.some(x => (x.value !== undefined && !Array.isArray(x.value)) || (Array.isArray(x.value) && x.value.some(y => y))) &&
                            <span className="margin-right-s badge badge-pill badge-primary padding-s d-flex">
                                <div style={{cursor: 'pointer'}} onClick={() => {
                                    setColumnFilters([]);
                                }}>
                                    <EmblaIcon additionalClasses={"align-items-center"} iconName={EmblaIcons.Close}/>
                                </div>
                            </span>
                        }
                        {!filterOptions.hideColumnFilter &&
                            <DynamicButton onClick={() => setColumnSlideInIsOpen(true)}
                                           additionalClasses={"d-flex margin-right-m"}>
                                <DynamicButton.TextIconItem text={Localizer.dashboard_columns()}
                                                            iconName={EmblaIcons.Graph}
                                />
                            </DynamicButton>
                        }
                        {isAsync && filterOptions.onSearchChangeCallback && !filterOptions.hideSearchLabel &&
                            <div className="flex items-center ">
                                <SearchField
                                    additionalClasses={"min-w-300"}
                                    callback={filterOptions?.onSearchChangeCallback}
                                />
                            </div>
                        }
                    </div>
                </div>

                <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToHorizontalAxis]}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                >
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    <SortableContext
                                        items={columnOrder}
                                        strategy={horizontalListSortingStrategy}>
                                        {headerGroup.headers.map(header => (
                                            <ReactTableHeader key={header.id}
                                                              header={header}
                                                              enableColumnGrouping={enableColumnGrouping}
                                                              enableColumnResizing={enableColumnResizing}/>
                                        ))}
                                    </SortableContext>
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length}
                                               className="text-center">
                                        <Loading isLoading={isLoading} text={Localizer.global_gettingData()}/>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows?.length ? (
                                table.getRowModel().rows?.map((row) => (
                                    <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}
                                              style={{backgroundColor: rowColorCallback ? rowColorCallback(row) : "unset"}}>
                                        {row.getVisibleCells().map((cell) => (
                                            <SortableContext
                                                key={cell.id}
                                                items={columnOrder}
                                                strategy={horizontalListSortingStrategy}>
                                                <DragAlongCell
                                                    cell={cell}
                                                    row={row}
                                                    enableColumnGrouping={enableColumnGrouping}/>
                                            </SortableContext>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="text-center">
                                        {Localizer.noResults()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DndContext>
            </Container.Body>
            <Container.Footer additionalClasses="flex-center flex-container-column">
                {!isAsync &&
                    <Pagination
                        handlePagination={(page) => table.setPageIndex(page - 1)}
                        paginationModel={new PaginationModel(table.getState().pagination.pageIndex + 1, table.getState().pagination.pageSize, table.getRowCount())}
                    />
                }
                {isAsync && paginationOverride &&
                    <Pagination
                        handlePagination={(page) => paginationOverride.onPageChangeCallback(page)}
                        paginationModel={new PaginationModel(paginationOverride.pageIndex, paginationOverride.pageLength, paginationOverride.totalRecords)
                        }
                    />
                }
                {!isAsync &&
                    <div className={"margin-top-s"}>
                        <ReactSyncTablePaginationCount key={table.getState().pagination.pageIndex} table={table}/>
                    </div>
                }
            </Container.Footer>
            {columnSlideInIsOpen &&
                createPortal(
                    <SlideIn
                        id={columnSettingsModalTargetId}
                        defaultOpen={true}
                        title={Localizer.dashboard_columns()}
                        bodyContent={<TableColumnSettings table={table}/>}
                        actionOnCloseCallback={() => setColumnSlideInIsOpen(false)}
                    />,
                    document.body
                )
            }
        </div>
    );
};

export default ReactBaseTable;
